import { useState, useEffect, useCallback, useRef } from 'react'
import { useCreateBot } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBottomNav } from '@/shared/contexts/BottomNavContext'
import { validateBotToken } from '@/shared/utils/format'

interface AddBotViewProps {
  isOpen: boolean
  onClose: () => void
  ownerId: string
}

export default function AddBotView({ isOpen, onClose, ownerId }: AddBotViewProps) {
  const [botToken, setBotToken] = useState('')
  const [error, setError] = useState('')

  const { hapticNotification, showPopup, showMainButton, hideMainButton, setMainButtonLoading, showBackButton, hideBackButton } = useTelegram()
  const { hideBottomNav, showBottomNav } = useBottomNav()
  const createBot = useCreateBot()

  // Use ref to store the latest handleSubmit without causing re-renders
  const handleSubmitRef = useRef<(() => Promise<void>) | null>(null)

  const handleSubmit = useCallback(async () => {
    setError('')

    // Validate bot token
    if (!validateBotToken(botToken)) {
      setError('Неверный формат токена')
      hapticNotification('error')
      return
    }

    setMainButtonLoading(true)

    try {
      await createBot.mutateAsync({
        owner_id: ownerId,
        bot_token: botToken,
      })

      hapticNotification('success')

      // Close view
      setBotToken('')
      setError('')

      // Force blur input to reset keyboard state
      const activeElement = document.activeElement
      if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
        activeElement.blur()
      }

      // Clean up UI state
      hideMainButton()
      hideBackButton()
      showBottomNav()

      onClose()

      // Show native Telegram success popup if available
      if (showPopup) {
        try {
          await showPopup({
            title: '✅ Успешно',
            message: 'Бот успешно добавлен и зарегистрирован в системе',
            buttons: [{ type: 'ok' }]
          })
        } catch (e) {
          console.log('Popup not supported, bot added successfully')
        }
      }
    } catch (err: any) {
      console.error('Failed to create bot:', err)
      const errorMessage = err.response?.data?.error || 'Не удалось добавить бота'
      setError(errorMessage)
      hapticNotification('error')

      // Show native Telegram error popup
      if (showPopup) {
        try {
          await showPopup({
            title: '❌ Ошибка',
            message: errorMessage,
            buttons: [{ type: 'ok' }]
          })
        } catch (e) {
          console.log('Popup not supported, showing inline error')
        }
      }
    } finally {
      setMainButtonLoading(false)
    }
  }, [botToken, ownerId, createBot, hapticNotification, showPopup, onClose, setMainButtonLoading, hideMainButton, hideBackButton, showBottomNav])

  // Update ref whenever handleSubmit changes
  useEffect(() => {
    handleSubmitRef.current = handleSubmit
  }, [handleSubmit])

  // Stable wrapper function that always calls the latest handleSubmit
  const stableHandleSubmit = useCallback(async () => {
    if (handleSubmitRef.current) {
      await handleSubmitRef.current()
    }
  }, [])

  // Setup MainButton and BackButton when view opens
  useEffect(() => {
    if (isOpen) {
      showMainButton('Добавить бота', stableHandleSubmit, '#5288c1', '#ffffff')
      showBackButton(onClose)
      hideBottomNav()
    } else {
      // Clean up when closing
      hideMainButton()
      hideBackButton()
      showBottomNav()
    }

    return () => {
      // Force blur any focused input to reset keyboard state in Layout
      const activeElement = document.activeElement
      if (activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement) {
        activeElement.blur()
      }

      hideMainButton()
      hideBackButton()
      showBottomNav()
    }
  }, [isOpen, showMainButton, hideMainButton, showBackButton, hideBackButton, stableHandleSubmit, onClose, hideBottomNav, showBottomNav])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-tg-bg z-[200] overflow-y-auto pt-safe">
      {/* Content */}
      <div className="flex flex-col items-center px-4 pt-[200px] pb-32">
        {/* Title */}
        <h2 className="text-2xl font-bold text-white mb-3">Добавить бота</h2>

        {/* Description */}
        <p className="text-[#8e96a0] text-center text-sm mb-8 max-w-md">
          Введите токен вашего бота для добавления в систему
        </p>

        {/* Input Field */}
        <div className="w-full max-w-md">
          <input
            type="text"
            value={botToken}
            onChange={(e) => setBotToken(e.target.value)}
            placeholder="123456789:ABCdefGHI..."
            className={`w-full px-4 py-3 bg-[#242f3d] text-white placeholder-[#6d7885] rounded-xl border ${
              error ? 'border-red-500' : 'border-transparent'
            } focus:outline-none focus:border-[#5288c1] transition-colors`}
          />

          {/* Helper text or error */}
          {error ? (
            <p className="mt-2 text-sm text-red-400">{error}</p>
          ) : (
            <p className="mt-2 text-sm text-[#6d7885]">
              Получите токен у @BotFather в Telegram
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
